import { useCallback, useMemo } from "react";
import { shallowEqual, useSelector } from "react-redux";

import { useThunkDispatch } from "@/redux/dispatcher";
import {
    clearFacetValues,
    selectFacetValue
} from "@/redux/modules/value/search/search.actions";
import { getSelectedValuesForFacet } from "@/redux/modules/value/search/search.selectors";
import { getInvalidationsForComponent } from "@/utils/search/invalidation.utils";

import { FacetComponentProps } from "../../@types/facet.types";
import { FacetType } from "../../@types/facetType.types";
import { getOperator } from "../../facet.configuration";
import { RefinementSort } from "../../facetSortBy";
import { ColorswatchFacetComponent } from "./colorswatchFacet.component";

export type ColorswatchFacetDisplayValue = {
    label: string;
    refined: boolean;
    url: string;
    onRefine: () => void;
};

const ColorswatchFacet = ({
    facet,
    component,
    sendEvent
}: FacetComponentProps<FacetType.COLORSWATCH>) => {
    const dispatch = useThunkDispatch();

    const selectedValues = useSelector(
        getSelectedValuesForFacet(component, facet.name),
        shallowEqual
    );

    const operator = useMemo(() => getOperator(facet.name), [facet.name]);
    const invalidations = useMemo(
        () => getInvalidationsForComponent(component),
        [component]
    );

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    const createOnRefine = useCallback(
        (refined: boolean, facetName: string, facetValue: string) => () =>
            dispatch(
                refined
                    ? clearFacetValues(
                          component,
                          facetName,
                          [facetValue],
                          invalidations
                      )
                    : selectFacetValue(
                          component,
                          {
                              name: facetName,
                              operator: operator
                          },
                          facetValue,
                          invalidations,
                          sendEvent
                      )
            ),
        [component, operator, invalidations, dispatch]
    );

    const colorswatchFacetDisplayValues: Array<ColorswatchFacetDisplayValue> =
        facet.values
            .map((colorswatchValue) => {
                const refined = selectedValues.includes(colorswatchValue.value);
                return {
                    label: colorswatchValue.name,
                    url: colorswatchValue.url,
                    refined: refined,
                    onRefine: createOnRefine(
                        refined,
                        facet.name,
                        colorswatchValue.value
                    )
                };
            })
            .sort(RefinementSort.IS_REFINED_DESC);

    return <ColorswatchFacetComponent values={colorswatchFacetDisplayValues} />;
};

export { ColorswatchFacet };
